<template>
    <div class="ViewMapByCoordinates-MAIN">
        <div v-if="showMap" ref="mapEl" class="map"></div>
        <img 
            v-else
            src="../assets/mapa-base.png"
            class="map-base"
            alt="No se encontró ninguna promoción cerca"
        />
    </div>
</template>

<script>
import { Loader } from "@googlemaps/js-api-loader";

export default {
    name: "ViewMapByCoordinates",

    props: {
        apiKey: { type: String, required: true },
        items: { type: Array, required: true },
        zoom: { type: Number, default: 13 },
            fallbackCenter: {
            type: Object,
            default: () => ({ lat: 19.432608, lng: -99.133209 })
        },
        radius: { type: Number, default: 5000 },
    },

    data() {
        return {
            map: null,
            infoWindow: null,
            markers: [],
            userMarker: null,
            userCircle: null,
            showMap: false,
            currentCenter: null,
        };
    },

    methods: {
        async loadGoogle() {
            const loader = new Loader({
                apiKey: this.apiKey,
                libraries: [],
                version: "quarterly"
            });

            return await loader.load();
        },

        parseCoordinates(raw) {
            if (!raw) return null;

            // ya viene como objeto
            if (
                typeof raw === "object" &&
                raw !== null &&
                raw.lat !== undefined &&
                raw.lng !== undefined
            ) {
                const lat = parseFloat(raw.lat);
                const lng = parseFloat(raw.lng);

                if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

                return { lat, lng };
            }

            let value = raw;

            // intenta parsear JSON/stringified array
            try {
                value = JSON.parse(raw);

                // caso: ["19.43,-99.13"]
                if (Array.isArray(value) && typeof value[0] === "string") {
                    value = value[0];
                }

                // caso: [[19.43,-99.13]]
                if (Array.isArray(value) && Array.isArray(value[0])) {
                    return {
                        lat: parseFloat(value[0][0]),
                        lng: parseFloat(value[0][1])
                    };
                }

                // caso: [{lat,lng}]
                if (
                    Array.isArray(value) &&
                    value[0] &&
                    typeof value[0] === "object"
                ) {
                    return {
                        lat: parseFloat(value[0].lat),
                        lng: parseFloat(value[0].lng)
                    };
                }

                // caso: [19.43,-99.13]
                if (
                    Array.isArray(value) &&
                    value.length >= 2
                ) {
                    return {
                        lat: parseFloat(value[0]),
                        lng: parseFloat(value[1])
                    };
                }

            } catch(e) {
                // no era JSON válido, continúa normal
            }

            // caso clásico: "19.43,-99.13"
            const parts = String(value)
                .split(",")
                .map(x => parseFloat(x.trim()));

            if (parts.length !== 2) return null;

            const [lat, lng] = parts;

            if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
            if (lat < -90 || lat > 90) return null;
            if (lng < -180 || lng > 180) return null;

            return { lat, lng };
        },

        distanceMeters(a, b) {
            const R = 6371000;
            const toRad = x => (x * Math.PI) / 180;

            const dLat = toRad(b.lat - a.lat);
            const dLng = toRad(b.lng - a.lng);

            const lat1 = toRad(a.lat);
            const lat2 = toRad(b.lat);

            const s =
                Math.sin(dLat / 2) ** 2 +
                Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

            return 2 * R * Math.asin(Math.sqrt(s));
        },

        getValidItems(userCenter = null) {
            return (this.items || [])
                .map(item => {
                    const coords = this.parseCoordinates(item.coordenadas);
                    if (!coords) return null;

                    const parsed = {
                        ...item,
                        lat: coords.lat,
                        lng: coords.lng
                    };

                    if (userCenter) {
                        parsed.distance_m = this.distanceMeters(userCenter, coords);

                        if (parsed.distance_m > this.radius) {
                            return null;
                        }
                    }

                    return parsed;
                })
                .filter(Boolean);
        },

        async initMap(center) {
            await this.loadGoogle();

            const google = window.google;

            this.map = new google.maps.Map(this.$refs.mapEl, {
                center,
                zoom: this.zoom,

                mapTypeControl: false,
                streetViewControl: false,

                gestureHandling: 'none',
                zoomControl: false,
                keyboardShortcuts: false,

                fullscreenControl: false,
            });

            this.infoWindow = new google.maps.InfoWindow();

            this.renderMarkers(center);
        },

        renderMarkers(userCenter = null) {
            const google = window.google;

            this.markers.forEach(marker => marker.setMap(null));
            this.markers = [];

            const validItems = this.getValidItems(userCenter);

            const bounds = new google.maps.LatLngBounds();

            if (userCenter) {
                this.userMarker = new google.maps.Marker({
                    map: this.map,
                    position: userCenter,
                    title: "Mi ubicación",
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 9,
                        fillColor: "#4285F4",
                        fillOpacity: 1,
                        strokeWeight: 2,
                        strokeColor: "#fff"
                    }
                });

                this.userCircle?.setMap(null);

                this.userCircle = new google.maps.Circle({
                    map: this.map,
                    center: userCenter,
                    radius: this.radius,
                    fillColor: "#4285F4",
                    fillOpacity: 0.12,
                    strokeColor: "#4285F4",
                    strokeOpacity: 0.6,
                    strokeWeight: 2,
                    clickable: false
                });

                bounds.extend(userCenter);
            }

            validItems.forEach(item => {
                const position = {
                    lat: item.lat,
                    lng: item.lng
                };

                const marker = new google.maps.Marker({
                    map: this.map,
                    position,
                    title: item.nombre || "Cupón"
                });

                marker.addListener("click", () => {
                    const distance = userCenter
                        ? `${Math.round(this.distanceMeters(userCenter, position))} m`
                        : "";

                    const img = item.url_banner
                        ? `<img src="${item.url_banner}" style="width:180px;max-width:100%;border-radius:6px;margin-top:6px;" />`
                        : "";

                    const url = `${window.location.origin}/cupon/?cupon=${item.id}`;

                    this.infoWindow.setContent(`
                        <div style="min-width:220px">
                        <strong>${item.nombre || "Cupón"}</strong><br/>
                        ${item.descuento || ""}<br/>
                        ${distance}<br/>
                        ${img}<br/>
                        <a href="${url}">Ver cupón</a>
                        </div>
                    `);

                    this.infoWindow.open(this.map, marker);
                });

                this.markers.push(marker);
                bounds.extend(position);
            });

            if (validItems.length > 0) {
                this.map.fitBounds(bounds, 48);
            }
        },

        getLocation() {
            const useCenter = (center) => {
                this.currentCenter = center;

                const validItems = this.getValidItems(center);

                if (validItems.length === 0) {
                    this.showMap = false;
                    return;
                }

                this.showMap = true;

                this.$nextTick(() => {
                    this.initMap(center);
                });
            };

            if (!("geolocation" in navigator)) {
                useCenter(this.fallbackCenter);
                return;
            }

            navigator.geolocation.getCurrentPosition(
                pos => {
                    useCenter({
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude
                    });
                },
                () => {
                    useCenter(this.fallbackCenter);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000
                }
            );
        }
    },

    mounted() {
        this.getLocation();
    }
};
</script>

<style scoped>
.ViewMapByCoordinates-MAIN {
    width: 100%;
}

.map {
    width: 100%;
    height: 300px;
    border: 0;
}

.map-base {
    width: 100%;
    height: 300px;
    object-fit: contain;
    display: block;
}
</style>